import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { api } from "../../api/client";
import type { LabelElement, LabelTemplate, LabelType } from "../../api/types";
import { DOTS_PER_MM, FIELD_PREVIEW, FONT_CSS } from "./constants";

interface CanvasProps {
  labelType: LabelType;
  template: LabelTemplate;
  selectedId: string | null;
  previewBackgroundId: string | null;
  onSelect: (id: string | null) => void;
  onMove: (id: string, x: number, y: number) => void;
}

const ZOOM = 1;
const SNAP = 5;

function snap(v: number): number {
  return Math.round(v / SNAP) * SNAP;
}

function elementPreviewText(el: LabelElement): string {
  if (el.kind === "qr") return "QR";
  if (el.source === "static") return el.content ?? "";
  return FIELD_PREVIEW[el.field ?? ""] ?? `{${el.field}}`;
}

function elementWidthPx(el: LabelElement): number {
  if (el.kind === "qr") return el.scale * 30;
  const text = elementPreviewText(el);
  return Math.max(text.length * el.scale * 0.6, 20);
}

function elementHeightPx(el: LabelElement): number {
  if (el.kind === "qr") return el.scale * 30;
  return el.scale + 8;
}

export function Canvas({
  labelType,
  template,
  selectedId,
  previewBackgroundId,
  onSelect,
  onMove,
}: CanvasProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const widthPx = template.width_mm * DOTS_PER_MM * ZOOM;
  const heightPx = template.height_mm * DOTS_PER_MM * ZOOM;

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>, el: LabelElement) {
    e.stopPropagation();
    onSelect(el.id);
    if (!ref.current) return;
    const startRect = ref.current.getBoundingClientRect();
    const startX = e.clientX - startRect.left - el.x;
    const startY = e.clientY - startRect.top - el.y;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);

    function onMove2(ev: PointerEvent) {
      if (!ref.current) return;
      const r = ref.current.getBoundingClientRect();
      const nx = snap(ev.clientX - r.left - startX);
      const ny = snap(ev.clientY - r.top - startY);
      onMove(el.id, Math.max(0, nx), Math.max(0, ny));
    }
    function onUp() {
      window.removeEventListener("pointermove", onMove2);
      window.removeEventListener("pointerup", onUp);
    }
    window.addEventListener("pointermove", onMove2);
    window.addEventListener("pointerup", onUp);
  }

  return (
    <div
      ref={ref}
      className="app-theme-neutral"
      data-mantine-color-scheme="light"
      onPointerDown={() => onSelect(null)}
      style={{
        position: "relative",
        width: widthPx,
        height: heightPx,
        background: "#fff",
        border: "1px solid #e5e5e5",
        boxShadow: "0 1px 4px rgba(0,0,0,.05)",
        overflow: "hidden",
        userSelect: "none",
      }}
    >
      {previewBackgroundId && (
        <img
          src={api.labelBackgroundUrl(labelType, previewBackgroundId)}
          alt=""
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            objectFit: "contain", opacity: 0.6, pointerEvents: "none",
          }}
        />
      )}
      {template.elements.map((el) => {
        const isSelected = el.id === selectedId;
        const w = elementWidthPx(el);
        const h = elementHeightPx(el);
        const fontStyle = el.kind === "text" ? FONT_CSS[el.font] : null;
        return (
          <div
            key={el.id}
            onPointerDown={(e) => handlePointerDown(e, el)}
            style={{
              position: "absolute",
              left: el.x, top: el.y,
              width: w, height: h,
              transform: el.kind === "text" && el.rotation
                ? `rotate(${el.rotation}deg)` : undefined,
              transformOrigin: "center",
              border: isSelected ? "2px solid #228be6" : "1px dashed #aaa",
              padding: 2,
              background: isSelected ? "rgba(34,139,230,.08)" : "rgba(255,255,255,.6)",
              cursor: "move",
              fontFamily: fontStyle?.family,
              fontWeight: el.kind === "text" && el.bold ? 700 : 400,
              fontSize: el.kind === "text" ? Math.max(8, el.scale) : 12,
              color: el.kind === "qr" ? "#000" : "#0f172a",
              display: "flex", alignItems: "center", justifyContent: "center",
              whiteSpace: "nowrap",
            }}
          >
            {el.kind === "qr" ? "QR" : elementPreviewText(el)}
          </div>
        );
      })}
      <div
        style={{
          position: "absolute", bottom: 4, right: 8, fontSize: 11, color: "#94a3b8",
        }}
      >
        {template.width_mm}×{template.height_mm} mm · {widthPx}×{heightPx} {t("labelEditor.dots")}
      </div>
    </div>
  );
}
